import { TEST_IDS } from '@va/constants';
import { PlayIcon, ReplayIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { Button, ButtonColors } from '@va/ui/design-system';
import { isWixMiniApp } from '@va/util/helpers';
import { DashboardAppWorker } from '@va/util/misc';
import classNames from 'classnames';
import React, { useRef } from 'react';
import { useHistory } from 'react-router';
import { RecordingsOperationsButton } from '../RecordingsOperationsButton';
import { useMarkSeenRecording } from '../apiClient';

type ActionsCellProps = {
  recordingId: string;
  seen: boolean;
  star: boolean;
  hasComment: boolean;
  seenButtonColor?: ButtonColors;
  size?: 'small' | 'medium';
};

export const ActionsCell: React.FC<ActionsCellProps> = ({
  recordingId,
  seen,
  star,
  hasComment,
  seenButtonColor = 'tertiary',
  size = 'medium',
}) => {
  const history = useHistory();
  const translate = useTranslate();
  const cellRef = useRef<HTMLDivElement>(null);

  const { execute: markSeen } = useMarkSeenRecording([recordingId]);

  return (
    <div
      className={classNames('flex', {
        'gap-2': size === 'medium',
        'gap-1.5': size === 'small',
      })}
      ref={cellRef}
    >
      <Button
        tooltip={seen ? translate('all.recordings.replay') : translate('all.recordings.play')}
        data-testid={seen ? TEST_IDS.generic.buttons.replay : TEST_IDS.generic.buttons.play}
        className={classNames({
          'w-9! h-9! p-2.5!': size === 'small',
        })}
        onClick={(event) => {
          // VISA-11501 - track click on view recording
          if (isWixMiniApp()) {
            DashboardAppWorker.trackEvent({
              category: 'Recording Main View',
              action: 'onclick',
              label: 'View Recording',
              value: 'Button',
            });
          }

          event.stopPropagation();

          if (!seen) {
            markSeen(true);
          }
          history.push(`/behaviour/recordings/${recordingId}`, { from: history.location.pathname });
        }}
        color={seen ? seenButtonColor : 'primary'}
        icon={(_, color) =>
          seen ? <ReplayIcon color={color} /> : <PlayIcon className='w-22px h-22px' color={color} />
        }
      />
      <RecordingsOperationsButton
        className={classNames({
          'w-9 h-9 p-2.5!': size === 'small',
        })}
        buttonColor={seenButtonColor}
        hasComment={hasComment}
        seen={seen}
        star={star}
        recordingId={recordingId}
      />
    </div>
  );
};

export default ActionsCell;
